import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { MapPin, Phone, Mail, Send, Instagram, Linkedin, Facebook, Youtube, Loader2 } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { ContactAPI } from '../lib/api';
import ColorAmbient from '../components/ColorAmbient';
import PrinterStrip from '../components/PrinterStrip';
import RegistrationMark from '../components/RegistrationMark';

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing fields', description: 'Please complete name, email and message.' });
      return;
    }
    setSubmitting(true);
    try {
      await ContactAPI.create(form);
      toast({ title: 'Message sent', description: 'We will get back to you within 24 hours.' });
      setForm({ name: '', company: '', phone: '', email: '', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const desc = Array.isArray(detail) ? detail[0]?.msg || 'Please check the form.' : detail || 'Could not send the message.';
      toast({ title: 'Error', description: desc });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-28 pb-24 bg-white min-h-screen relative overflow-hidden">
      <ColorAmbient variant="warm" intensity="medium" />

      {/* Registration marks */}
      <RegistrationMark className="absolute top-24 left-6 opacity-40" size={24} />
      <RegistrationMark className="absolute top-24 right-6 opacity-40" size={24} />

      {/* Hero */}
      <section className="container-pn relative">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Get in touch</p>
          <h1 className="text-5xl md:text-7xl font-black text-[#050F2E] tracking-tight">
            Contact{' '}
            <span className="pn-gradient-text">
              us
            </span>
          </h1>
          <p className="mt-5 text-lg text-slate-600">
            See the best way to contact us — by phone, email, or form. We&apos;re here to help.
          </p>
          <div className="mt-6 max-w-md">
            <PrinterStrip className="opacity-90" />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="container-pn mt-14 grid lg:grid-cols-12 gap-10">
        {/* Info */}
        <div className="lg:col-span-5 space-y-5">
          <div className="bg-[#0A2A63] text-white rounded-3xl p-7 shadow-xl">
            <p className="text-[10px] uppercase tracking-widest opacity-70 font-bold flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> Where are we located</p>
            <p className="mt-3 text-lg font-bold leading-snug">{contactInfo.address}</p>
          </div>
          <div className="bg-slate-50 rounded-3xl p-7 border border-slate-100">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> Contact Information</p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-white shadow-sm flex items-center justify-center"><Mail className="h-4 w-4 text-[#0A2A63]" /></span>
                <a href={`mailto:${contactInfo.email}`} className="font-semibold text-[#050F2E] hover:text-[#0A2A63]">{contactInfo.email}</a>
              </li>
              <li className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-white shadow-sm flex items-center justify-center"><Phone className="h-4 w-4 text-[#0A2A63]" /></span>
                <a href={`tel:${contactInfo.phone.replace(/\D/g, '')}`} className="font-semibold text-[#050F2E] hover:text-[#0A2A63]">{contactInfo.phone}</a>
              </li>
            </ul>
          </div>
          <div className="bg-slate-50 rounded-3xl p-7 border border-slate-100">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Existing Customer Support</p>
            <a href={`mailto:${contactInfo.support}`} className="mt-3 inline-flex items-center gap-2 text-base font-bold text-[#050F2E] hover:text-[#0A2A63]">
              <Mail className="h-4 w-4" /> {contactInfo.support}
            </a>
          </div>

          <div className="bg-white rounded-3xl p-7 border border-slate-100">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-4">Follow us</p>
            <div className="flex items-center gap-3">
              {[
                { href: contactInfo.social.instagram, Icon: Instagram, label: 'Instagram' },
                { href: contactInfo.social.linkedin, Icon: Linkedin, label: 'Linkedin' },
                { href: contactInfo.social.facebook, Icon: Facebook, label: 'Facebook' },
                { href: contactInfo.social.youtube, Icon: Youtube, label: 'Youtube' },
              ].map(({ href, Icon, label }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer"
                  className="h-11 w-11 rounded-full bg-slate-100 hover:bg-[#0A2A63] hover:text-white flex items-center justify-center transition-colors">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="lg:col-span-7 bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-slate-100">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Send us a message</p>
          <h2 className="text-3xl md:text-4xl font-black text-[#050F2E] tracking-tight">We&apos;re here to help.</h2>
          <p className="mt-2 text-slate-600">Send us a message and we will get back to you within 24 hours.</p>

          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Name *</Label>
              <Input id="name" name="name" value={form.name} onChange={onChange} className="mt-2 h-12 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="company" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Company</Label>
              <Input id="company" name="company" value={form.company} onChange={onChange} className="mt-2 h-12 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Phone</Label>
              <Input id="phone" name="phone" value={form.phone} onChange={onChange} className="mt-2 h-12 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Email *</Label>
              <Input id="email" type="email" name="email" value={form.email} onChange={onChange} className="mt-2 h-12 rounded-xl" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="message" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Message *</Label>
              <Textarea id="message" name="message" rows={5} value={form.message} onChange={onChange} className="mt-2 rounded-xl resize-none" />
            </div>
          </div>

          <div className="mt-7 flex items-center justify-between flex-wrap gap-4">
            <p className="text-xs text-slate-500">By submitting, you agree to our privacy policy.</p>
            <Button type="submit" disabled={submitting} className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-12 px-7 font-bold btn-transition">
              {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>) : (<><Send className="h-4 w-4 mr-2" /> Submit</>)}
            </Button>
          </div>
        </form>
      </section>
    </main>
  );
};

export default Contact;
